import { useEffect, useState } from "react";
import { useAppContext } from "../AppContext";
import { PaymentFrequency, ProductTypes, Device, CartItem, CardMachine, DeliveryPackageNames } from "../file.interface";
import { formatAsEuro } from "./helpers";
import { Routing } from "../file.interface";

export interface Unit {
    unitName: string;
    price: number;
    formattedPrice: string;
    paymentFrequency: PaymentFrequency;
}

export interface Module {
    moduleName: string;
    units: Unit[] | [];
}

export interface Section {
    sectionName: string,
    modules: Module[] | []
}

const defaultSection = {
    sectionName: "",
    modules: []
}


export const useCostCalculator = () => {
    //
    const [sitesCost, setSitesCost] = useState<Section[]>([defaultSection]);
    const [deliveryInstallationCost, setDeliveryInstallationCost] = useState<Section[]>([defaultSection]);
    const [productConfCost, setProductConfCost] = useState<Section[]>([defaultSection]);
    const [monthlyCost, setMonthlyCost] = useState<number>(0);
    const [oneOffCost, setOneOffCost] = useState<number>(0);

    const {
        routing,
        selectedPOSSolution,
        selectedContractDuration,
        sites,
        selectedDeliveryPackage,
        selectedProductConfPackage,
        selectedOngoingSupportPackage
    } = useAppContext();

    const calculateSitesCost = () => {

        let sitesMonthlyCost: number = 0;

        let _sites = sites.map((site) => {
            let epos: Device[] = site.cart.filter(
                (product): product is Device => product.type === ProductTypes.Epos
            );

            let terminals: CardMachine[] = site.cart.filter((product): product is Device => product.type === ProductTypes.Terminals);
            let terminalsWithLicence = terminals.filter((product) => "hasLicence" in product && product.hasLicence);
            let accessories = site.cart.filter((product) => product.type === ProductTypes.Accesories);
            let eposModule = {
                moduleName: "Devices",
                units: epos.map((device) => {
                    let cardMachinePrice = device.cardMachine?.prices[selectedContractDuration] ?? 0;
                    let cardMachineName = device.cardMachine?.name;
                    let _price: number = device.prices[selectedContractDuration] + cardMachinePrice;
                    sitesMonthlyCost += _price;
                    return {
                        unitName: `${device.name} + ${cardMachineName}`,
                        price: _price,
                        formattedPrice: `${formatAsEuro(_price)} ${device.paymentFrequency}`,
                        paymentFrequency: device.paymentFrequency,
                    }
                })
            }

            let licences = calculateSoftwareCost(epos, terminalsWithLicence);
            sitesMonthlyCost += licences.total;
            let licencesModule = {
                moduleName: "Licences",
                units: licences.units
            }

            let terminalsModule = {
                moduleName: `${routing === Routing.Epos ? 'Additional ' : ''}Card Machines`,
                units: terminals.map((terminal) => {
                    let _price = terminal.prices[selectedContractDuration];
                    sitesMonthlyCost += _price;
                    return {
                        unitName: terminal.name,
                        price: _price,
                        formattedPrice: `${formatAsEuro(_price)} ${terminal.paymentFrequency}`,
                        paymentFrequency: terminal.paymentFrequency,
                    }
                })
            }
            let accessoriesModule = {
                moduleName: "Additional Accessories",
                units: accessories.map((accessory) => {
                    let _price = accessory.prices[selectedContractDuration];
                    sitesMonthlyCost += _price;
                    return {
                        unitName: accessory.name,
                        price: _price,
                        formattedPrice: `${formatAsEuro(_price)} ${accessory.paymentFrequency}`,
                        paymentFrequency: accessory.paymentFrequency,
                    }
                })
            }

            let OSPrice = selectedOngoingSupportPackage?.getPrice(epos.length + terminals.length) ?? 0;
            sitesMonthlyCost += OSPrice;

            let ongoingSupportModule = {
                moduleName: "Ongoing Support",
                units: [
                    {
                        unitName: selectedOngoingSupportPackage?.packageName ?? "",
                        price: OSPrice,
                        formattedPrice: `${formatAsEuro(OSPrice)} ${selectedOngoingSupportPackage?.paymentFrequency}`,
                        paymentFrequency: selectedOngoingSupportPackage?.paymentFrequency ?? PaymentFrequency.Monthly
                    }
                ]
            };

            return {
                sectionName: site.name,
                modules: [
                    eposModule, terminalsModule, licencesModule, accessoriesModule, ongoingSupportModule
                ].filter((obj) => obj && obj.units.length > 0)
            }
        })

        setSitesCost(_sites);
        setMonthlyCost(sitesMonthlyCost);
    }

    const calculateSoftwareCost = (devices: Device[], terminals: CardMachine[]) => {
        let total: number = 0;
        let eposUnits: Unit[] = devices?.map((device, i) => {
            let _price = selectedPOSSolution?.getEPOSLicencePrice(i + 1) ?? 0;
            total += _price;
            return {
                unitName: `${device.name} Licence`,
                price: _price,
                formattedPrice: `${formatAsEuro(_price)} ${device.paymentFrequency}`,
                paymentFrequency: device.paymentFrequency
            }
        })

        let terminalsUnits: Unit[] = terminals?.map((device, i) => {
            let _price = selectedPOSSolution?.getTerminalsLicencePrice(devices.length + i + 1) ?? 0;
            total += _price;
            return {
                unitName: `${device.name} Licence`,
                price: _price,
                formattedPrice: `${formatAsEuro(_price)} ${device.paymentFrequency}`,
                paymentFrequency: device.paymentFrequency
            }
        })

        return { units: [...eposUnits, ...terminalsUnits], total };
    }

    const calculateDeliveryInstallationCost = () => {
        if (selectedDeliveryPackage) {
            let _formattedPrice = `${formatAsEuro(selectedDeliveryPackage?.price ?? 0)} ${selectedDeliveryPackage?.paymentFrequency}`;
            if (selectedDeliveryPackage.packageName === DeliveryPackageNames.buildYourOwn)
                _formattedPrice = selectedDeliveryPackage.priceDescription;
            setDeliveryInstallationCost([{
                sectionName: "",
                modules: [{
                    moduleName: "Delivery & Installation",
                    units: [{
                        unitName: selectedDeliveryPackage?.packageName ?? "",
                        price: selectedDeliveryPackage?.price ?? 0,
                        formattedPrice: _formattedPrice,
                        paymentFrequency: selectedDeliveryPackage?.paymentFrequency ?? PaymentFrequency.Single
                    }]
                }]
            }]);
        }
    }

    const calculateProductConfCost = () => {
        setProductConfCost([{
            sectionName: "",
            modules: [{
                moduleName: "Product Configuration",
                units: [{
                    unitName: selectedProductConfPackage?.packageName ?? "",
                    price: selectedProductConfPackage?.price ?? 0,
                    formattedPrice: `${formatAsEuro(selectedProductConfPackage?.price ?? 0)} ${selectedProductConfPackage?.paymentFrequency}`,
                    paymentFrequency: selectedProductConfPackage?.paymentFrequency ?? PaymentFrequency.Single
                }]
            }]
        }]);
    }

    useEffect(() => {
        calculateSitesCost();
        calculateDeliveryInstallationCost();
        calculateProductConfCost();
        //console.log("useeffect: main price-calculation 1")
    }, [selectedContractDuration]);

    useEffect(() => {
        let dicPrice: number = deliveryInstallationCost[0]?.modules[0]?.units[0]?.price ?? 0;
        let pccPrice: number = productConfCost[0]?.modules[0]?.units[0]?.price ?? 0;
        let oneOffCost: number = dicPrice + pccPrice;
        setOneOffCost(oneOffCost ?? 0);
        //console.log("useeffect: price-calculation 2")
    }, [deliveryInstallationCost, productConfCost]);

    return { sitesCost, deliveryInstallationCost, productConfCost, monthlyCost, oneOffCost }
}



export interface Calculator {
    averageTransactionAmount: number;
    processPerMonth: number;
    numberOfCardMachines: number;
}

export const useFeeCalculator = (initialParams: Calculator) => {
    const [params, setParams] = useState<Calculator>(initialParams);
    const [consumerDebitCharge, setConsumerDebitCharge] = useState<number>(0);
    const [consumerCreditCharge, setConsumerCreditCharge] = useState<number>(0);
    const [businessCardsCharge, setBusinessCardsCharge] = useState<number>(0);
    const [totalFeeCharge, setTotalFeeCharge] = useState<number>(0);
    const pci: number = 6;

    const calculate = () => {

        const { averageTransactionAmount, processPerMonth, numberOfCardMachines } = params;
        let atv = averageTransactionAmount
        let atm = processPerMonth;

        //let total_cost = 0;
        let transactions = atm / atv;
        let pci_charge = 6;

        //consumer debit
        let cd_revenue = (atm / 100) * 90;
        let cd_transaction = (transactions / 100) * 90;
        let cd_percentage = 0.25;
        let cd_auth = 0.03;
        let cd_charge = percentage(cd_revenue, cd_percentage) + (cd_transaction * cd_auth);
        cd_charge = cd_charge * numberOfCardMachines;
        //consumer credit
        let cc_revenue = atm / 100 * 8;
        let cc_transaction = (cd_transaction / 100) * 8;
        let cc_percentage = 0.79;
        let cc_auth = 0.03;
        let cc_charge = percentage(cc_revenue, cc_percentage) + (cc_transaction * cc_auth);
        cc_charge = cc_charge * numberOfCardMachines;
        //console.log(cc_charge);

        //business cards
        let bc_revenue = atm / 100 * 2;
        let bc_transaction = 4;
        let bc_percentage = 1.8;
        let bc_auth = 0.03;
        let bc_charge = percentage(bc_revenue, bc_percentage) + (bc_transaction * bc_auth);
        bc_charge = bc_charge * numberOfCardMachines;


        setConsumerCreditCharge(Number(cc_charge.toFixed(2)));
        setConsumerDebitCharge(Number(cd_charge.toFixed(2)));
        setBusinessCardsCharge(Number(bc_charge.toFixed(2)));
        let subtotal = cd_charge + cc_charge + bc_charge + pci_charge;
        setTotalFeeCharge(Number(subtotal.toFixed(2)));
    }

    function percentage(totalValue: number, partialValue: number) {
        return (totalValue * partialValue) / 100;
    }

    useEffect(() => {
        calculate();
        //console.log("useeffect: fee-calculator")
    }, [params]);

    const updateParams = (newParams: Calculator) => {
        setParams(newParams);
    }

    return { consumerCreditCharge, consumerDebitCharge, businessCardsCharge, pci, totalFeeCharge, updateParams, params }
}


